import { useState, useEffect, useCallback } from 'react';
import { DataTable, Button } from '@sundkortid/ui';
import type { PaginationMeta } from '@sundkortid/shared';
import { api } from '../../api/client';
import { AdminNav } from './AdminDashboardPage';

interface AuditLog {
  id: string;
  actorId: string | null;
  actorName: string | null;
  action: string;
  targetType: string;
  targetId: string;
  metadata: unknown;
  ipAddress: string | null;
  createdAt: string;
}

export function AdminAuditPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), perPage: '20' });
      if (actionFilter) params.set('action', actionFilter);

      const result = await api.getWithMeta<AuditLog[]>(`/admin/audit-logs?${params}`);
      setLogs(result.data);
      setMeta(result.meta ?? null);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { setPage(1); }, [actionFilter]);

  const columns = [
    {
      key: 'createdAt',
      header: 'Dagsetning',
      render: (row: AuditLog) => new Date(row.createdAt).toLocaleString('is-IS'),
    },
    { key: 'action', header: 'Aðgerð' },
    {
      key: 'actorName',
      header: 'Aðili',
      render: (row: AuditLog) => row.actorName ?? row.actorId ?? 'Kerfi',
    },
    { key: 'targetType', header: 'Tegund' },
    {
      key: 'targetId',
      header: 'Auðkenni',
      render: (row: AuditLog) => row.targetId.slice(0, 8) + '...',
    },
    {
      key: 'ipAddress',
      header: 'IP',
      render: (row: AuditLog) => row.ipAddress ?? '—',
    },
  ];

  const actionOptions = [
    'user.created', 'user.updated', 'user.deleted', 'user.suspended',
    'pass.purchased', 'pass.activated', 'pass.revoked', 'pass.adjusted',
    'redemption.created', 'redemption.reversed',
    'pool.created', 'pool.updated', 'pool.deleted',
    'payment.completed', 'payment.failed', 'payment.refunded',
    'auth.login', 'auth.logout', 'auth.password_reset',
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="max-w-6xl mx-auto flex flex-col gap-4">
          <h1 className="text-xl font-title font-bold text-primary">Sundkortið — Stjórnborð</h1>
          <AdminNav />
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">
        <h2 className="text-2xl font-title font-bold text-primary mb-6">Aðgerðaskrá</h2>

        <div className="flex flex-wrap gap-3 mb-4">
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">Allar aðgerðir</option>
            {actionOptions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="text-gray-500">Hleð gögnum...</p>
        ) : (
          <>
            <DataTable columns={columns} data={logs} keyExtractor={(row) => row.id} />
            {meta && meta.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Fyrri
                </Button>
                <span className="text-sm text-gray-600">Síða {meta.page} af {meta.totalPages}</span>
                <Button size="sm" variant="outline" disabled={page >= meta.totalPages} onClick={() => setPage(page + 1)}>
                  Næsta
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
